"use client";

import { NOT_ELIGIBLE, REJECTED } from "@/config/app-constant";
import { useWithdrawal } from "@/hooks/withdrawal/use-withdrawal";
import { WithdrawalLayoutContent } from "@/utils/schema/withdrawal-page-schema.utils";
import { WithdrawalEligibilityStatus } from "@/utils/withdrawal/withdrawal-eligibility-status.utils";

import FinancialSummary from "./financial-summary";
import PolicyDetails from "./policy-details";
import WithdrawalButton from "./withdrawal-button";

export interface WithdrawalData {
  policy: {
    organiser: string;
    policyNumber: string;
    coverType: string;
    coverLevel: string;
    destination: string;
    coverDates: string;
    travellers: string;
  };
  financial: {
    originalPremium: string;
    paidOn: string;
    cardLastFourDigits?: string;
  };
  withdrawalEligibilityStatus?: WithdrawalEligibilityStatus;
}

interface WithdrawalContentProps {
  content?: WithdrawalLayoutContent;
  data: WithdrawalData;
  onWithdraw?: (withdrawalReason: string) => void | Promise<void>;
}

export default function WithdrawalContent({
  content,
  data,
  onWithdraw = () => {},
}: Readonly<WithdrawalContentProps>) {
  const { policyData } = useWithdrawal();

  const eligibilityStatus =
    policyData?.withdrawalData.withdrawalEligibilityStatus;
  return (
    <div className="w-full">
      <PolicyDetails
        sectionTitle={content?.policyDetailsSectionTitle}
        labels={{
          organiser: content?.organiserLabel,
          policyNumber: content?.policyNumberLabel,
          coverType: content?.coverTypeLabel,
          coverLevel: content?.coverLevelLabel,
          destination: content?.destinationLabel,
          coverDates: content?.coverDatesLabel,
          travellers: content?.travellersLabel,
        }}
        organiser={data.policy.organiser}
        policyNumber={data.policy.policyNumber}
        coverType={data.policy.coverType}
        coverLevel={data.policy.coverLevel}
        destination={data.policy.destination}
        coverDates={data.policy.coverDates}
        travellers={data.policy.travellers}
        infoBoxRejectedRichText={content?.infoBoxRejectedRichText}
        infoBoxNotEligibleRichText={content?.infoBoxNotEligibleRichText}
        eligibleBadgeText={content?.eligibleBadgeText}
        notEligibleBadgeText={content?.notEligibleBadgeText}
        rejectedBadgeText={content?.rejectedBadgeText}
        withdrawnBadgeText={content?.withdrawnBadgeText}
        requestedBadgeText={content?.requestedBadgeText}
      />

      {eligibilityStatus !== REJECTED && eligibilityStatus !== NOT_ELIGIBLE && (
        <>
          <div className="border-t border-border-neutral-subtle mb-spacing-l" />
          <FinancialSummary
            sectionTitle={content?.refundSummarySectionTitle}
            finalRefundSectionTitle={content?.finalRefundSummarySectionTitle}
            originalPremiumLabel={content?.originalPremiumLabel}
            paidOnLabel={content?.paidOnLabel}
            infoBoxRichText={content?.infoBoxRichText}
            originalPremium={data.financial.originalPremium}
            paidOn={data.financial.paidOn}
          />
        </>
      )}

      <WithdrawalButton
        buttonText={content?.withdrawalButtonText}
        calculationNoteRichText={content?.calculationNoteRichText}
        processNoteRichText={content?.processNoteRichText}
        onWithdraw={onWithdraw}
        policyNumber={data.policy.policyNumber}
        cardLastFourDigits={data.financial.cardLastFourDigits}
        modalTitle={content?.modalTitle}
        modalIntroText={content?.modalIntroText}
        modalWarningText={content?.modalWarningText}
        modalRefundInfoText={content?.modalRefundInfoText}
        modalOptionalText={content?.modalOptionalText}
        modalReasonPlaceholder={content?.modalReasonPlaceholder}
        confirmButtonText={content?.confirmButtonText}
        cancelButtonText={content?.cancelButtonText}
        successTitle={content?.successTitle}
        successMessage={content?.successMessage}
        successAdditionalInfo={content?.successAdditionalInfo}
        closeButtonText={content?.closeButtonText}
      />
    </div>
  );
}
